import { useState, useEffect, useContext } from 'react';
import { AppContext } from '../App';
import { useI18n } from '../i18n/index.jsx';
import api from '../api';
import LanguageSelector from '../components/LanguageSelector';

/**
 * Onboarding Flow — без лишних вопросов
 * 
 * 1. Выбор языка
 * 2. Экран модели: "Фирма — ваш бизнес-шлюз"
 * 3. Скан паспорта (рекомендуется) → автозаполнение
 * 4. Автоназначение фирмы (если 1)
 * 5. Выбор: Только шлюз / Шлюз + легализация
 * 6. Подписание Gateway договора
 * 7. Dashboard активирован
 */

const STEPS = ['language', 'model', 'passport', 'company', 'type', 'gateway', 'done'];

export default function OnboardingFlow() {
  const { user, setPage, refreshUser, companies, currentCompany } = useContext(AppContext);
  const { t, lang, setLang } = useI18n();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Form data
  const [passportFile, setPassportFile] = useState(null);
  const [ocrData, setOcrData] = useState(null);
  const [ocrDocumentId, setOcrDocumentId] = useState(null);
  const [ocrStep, setOcrStep] = useState('idle'); // idle, uploading, processing, preview
  const [cooperationType, setCooperationType] = useState('gateway'); // gateway | full
  const [selectedCompany, setSelectedCompany] = useState(null);

  useEffect(() => {
    // Auto-select company if only one
    if (companies?.length === 1) {
      setSelectedCompany(companies[0]);
    }
  }, [companies]);

  const currentStepName = STEPS[step];

  const nextStep = () => setStep(s => Math.min(s + 1, STEPS.length - 1));
  const prevStep = () => setStep(s => Math.max(s - 1, 0));

  // Step handlers
  const handleLanguageSelect = () => {
    nextStep();
  };

  const handleModelUnderstand = () => {
    nextStep();
  };

  const handlePassportUpload = async (file) => {
    if (!file) {
      nextStep(); // Skip passport
      return;
    }
    
    setPassportFile(file);
    setOcrStep('uploading');
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      setOcrStep('processing');
      const res = await api.post('/documents/ocr-passport', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setOcrDocumentId(res.data?.document_id || null);
      setOcrData(res.data?.extracted || null);
      setOcrStep('preview');
    } catch (e) {
      setError(e.userMessage || 'Ошибка OCR');
      setOcrStep('idle');
    }
  };

  const handleApplyOcr = async () => {
    if (!ocrData || !ocrDocumentId) return;
    setLoading(true);
    try {
      await api.post(`/documents/${ocrDocumentId}/apply-to-profile`);
      await refreshUser();
      nextStep();
    } catch (e) {
      setError(e.userMessage || e.response?.data?.detail || 'Не удалось применить данные паспорта');
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySelect = () => {
    nextStep();
  };

  const handleTypeSelect = (type) => {
    setCooperationType(type);
    nextStep();
  };

  const handleSignGateway = async () => {
    if (!selectedCompany) {
      setError('Выберите фирму');
      return;
    }
    setLoading(true);
    try {
      await api.post('/gateway/sign', {
        company_id: selectedCompany.id,
        accepted_terms_1200: true,
        accepted_terms_350: true,
        accepted_terms_6pct: true,
        accepted_privacy: true,
        accepted_truth: true,
      });
      await refreshUser();
      nextStep();
    } catch (e) {
      setError(e.userMessage);
    } finally {
      setLoading(false);
    }
  };

  const finishOnboarding = () => {
    setPage('dashboard');
  };

  // Render steps
  return (
    <div className="onboarding-flow">
      {/* Progress indicator */}
      <div className="onboarding-progress">
        {STEPS.map((s, i) => (
          <div key={s} className={`progress-dot ${i <= step ? 'active' : ''} ${i === step ? 'current' : ''}`} />
        ))}
      </div>

      {error && (
        <div className="error-box" style={{ margin: '16px 0' }}>{error}</div>
      )}

      {/* Step 1: Language */}
      {currentStepName === 'language' && (
        <div className="onboarding-step">
          <h1>🌍 {t('onboarding.welcome')}</h1>
          <p className="subtitle">Выберите язык / Choose language</p>
          <LanguageSelector />
          <button className="btn btn-primary btn-large" onClick={handleLanguageSelect}>
            {t('common.next')} →
          </button>
        </div>
      )}

      {/* Step 2: Model explanation */}
      {currentStepName === 'model' && (
        <div className="onboarding-step">
          <div className="model-icon">🏢</div>
          <h1>{t('onboarding.model_title')}</h1>
          <p className="model-desc">{t('onboarding.model_desc')}</p>
          
          <div className="model-features">
            <div className="feature">
              <span className="feature-icon">📄</span>
              <span>{t('gateway.services.invoices')}</span>
            </div>
            <div className="feature">
              <span className="feature-icon">💰</span>
              <span>{t('gateway.services.payments')}</span>
            </div>
            <div className="feature">
              <span className="feature-icon">💸</span>
              <span>{t('gateway.services.payout')}</span>
            </div>
          </div>

          <button className="btn btn-primary btn-large" onClick={handleModelUnderstand}>
            {t('common.next')} →
          </button>
        </div>
      )}

      {/* Step 3: Passport scan */}
      {currentStepName === 'passport' && (
        <div className="onboarding-step">
          <div className="model-icon">🛂</div>
          <h1>{t('onboarding.scan_title')}</h1>
          <p className="subtitle">{t('onboarding.scan_desc')}</p>

          {ocrStep === 'idle' && (
            <>
              <label className="upload-zone">
                <input 
                  type="file" 
                  accept="image/*" 
                  capture="environment"
                  onChange={e => handlePassportUpload(e.target.files[0])}
                  style={{ display: 'none' }}
                />
                <div className="upload-content">
                  <span className="upload-icon">📷</span>
                  <span>{t('profile.scan_passport')}</span>
                </div>
              </label>
              <button className="btn btn-secondary" onClick={() => nextStep()}>
                {t('onboarding.scan_skip')}
              </button>
            </>
          )}

          {(ocrStep === 'uploading' || ocrStep === 'processing') && (
            <div className="ocr-progress">
              <div className={`ocr-step ${ocrStep === 'uploading' ? 'active' : 'done'}`}>
                📤 {t('profile.ocr_upload')}
              </div>
              <div className={`ocr-step ${ocrStep === 'processing' ? 'active' : ''}`}>
                🔍 {t('profile.ocr_recognize')}
              </div>
            </div>
          )}

          {ocrStep === 'preview' && ocrData && (
            <div className="ocr-preview">
              <h3>{t('profile.ocr_preview')}</h3>
              <div className="preview-fields">
                <div className="field-row">
                  <span>👤</span>
                  <span>{ocrData.first_name} {ocrData.last_name}</span>
                </div>
                {ocrData.passport_number && (
                  <div className="field-row">
                    <span>🛂</span>
                    <span>{ocrData.passport_number}</span>
                  </div>
                )}
                {ocrData.date_of_birth && (
                  <div className="field-row">
                    <span>📅</span>
                    <span>{ocrData.date_of_birth}</span>
                  </div>
                )}
                {ocrData.citizenship && (
                  <div className="field-row">
                    <span>🌍</span>
                    <span>{ocrData.citizenship}</span>
                  </div>
                )}
              </div>
              <button 
                className="btn btn-primary btn-large" 
                onClick={handleApplyOcr}
                disabled={loading}
              >
                {loading ? '⏳' : '✓'} {t('profile.ocr_apply')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Step 4: Company selection (auto if 1) */}
      {currentStepName === 'company' && (
        <div className="onboarding-step">
          <div className="model-icon">🏢</div>
          <h1>Ваша фирма</h1>
          
          {companies?.length === 1 ? (
            <div className="auto-company">
              <div className="company-card selected">
                <h3>{companies[0].short_name || companies[0].name}</h3>
                <p>NIP: {companies[0].nip}</p>
              </div>
              <p className="auto-note">✓ Автоматически назначена</p>
            </div>
          ) : (
            <div className="company-list">
              {companies?.map(c => (
                <div 
                  key={c.id}
                  className={`company-card ${selectedCompany?.id === c.id ? 'selected' : ''}`}
                  onClick={() => setSelectedCompany(c)}
                >
                  <h3>{c.short_name || c.name}</h3>
                  <p>NIP: {c.nip}</p>
                </div>
              ))}
            </div>
          )}

          <button 
            className="btn btn-primary btn-large" 
            onClick={handleCompanySelect}
            disabled={!selectedCompany && companies?.length > 1}
          >
            {t('common.next')} →
          </button>
        </div>
      )}

      {/* Step 5: Cooperation type */}
      {currentStepName === 'type' && (
        <div className="onboarding-step">
          <h1>{t('onboarding.type_title')}</h1>
          
          <div className="type-cards">
            <div 
              className={`type-card ${cooperationType === 'gateway' ? 'selected' : ''}`}
              onClick={() => setCooperationType('gateway')}
            >
              <div className="type-icon">🚀</div>
              <h3>{t('onboarding.type_gateway')}</h3>
              <p>{t('onboarding.type_gateway_desc')}</p>
              <ul>
                <li>✓ {t('gateway.services.invoices')}</li>
                <li>✓ {t('gateway.services.payments')}</li>
                <li>✓ {t('gateway.services.payout')}</li>
              </ul>
            </div>

            <div 
              className={`type-card ${cooperationType === 'full' ? 'selected' : ''}`}
              onClick={() => setCooperationType('full')}
            >
              <div className="type-icon">🛂</div>
              <h3>{t('onboarding.type_full')}</h3>
              <p>{t('onboarding.type_full_desc')}</p>
              <ul>
                <li>✓ Всё из Gateway</li>
                <li>✓ Oświadczenie</li>
                <li>✓ Zezwolenie</li>
                <li>✓ Karta Pobytu</li>
              </ul>
            </div>
          </div>

          <button 
            className="btn btn-primary btn-large" 
            onClick={() => handleTypeSelect(cooperationType)}
          >
            {t('common.next')} →
          </button>
        </div>
      )}

      {/* Step 6: Gateway signing */}
      {currentStepName === 'gateway' && (
        <div className="onboarding-step">
          <div className="model-icon">✍️</div>
          <h1>{t('gateway.title')}</h1>
          <p className="subtitle">{t('gateway.subtitle')}</p>

          <div className="gateway-terms">
            <div className="term">✓ {t('gateway.term_1200')}</div>
            <div className="term">✓ {t('gateway.term_350')}</div>
            <div className="term">✓ {t('gateway.term_6pct')}</div>
          </div>

          <button 
            className="btn btn-primary btn-large" 
            onClick={handleSignGateway}
            disabled={loading}
          >
            {loading ? '⏳ Подписываем...' : t('gateway.sign_btn')}
          </button>
        </div>
      )}

      {/* Step 7: Done */}
      {currentStepName === 'done' && (
        <div className="onboarding-step success">
          <div className="success-icon">🎉</div>
          <h1>{t('gateway.success_title')}</h1>
          <p>{t('gateway.success_msg')}</p>

          {cooperationType === 'full' && (
            <p className="full-note">
              Для легализации перейдите в раздел "Work Contract"
            </p>
          )}

          <button className="btn btn-primary btn-large" onClick={finishOnboarding}>
            {t('dashboard.cta')} →
          </button>
        </div>
      )}

      {/* Back button (except first and last step) */}
      {step > 0 && step < STEPS.length - 1 && (
        <button className="btn btn-link back-btn" onClick={prevStep}>
          ← {t('common.back')}
        </button>
      )}
    </div>
  );
}
